
import React, { useEffect, useState } from 'react';
import { useTranslationUtils } from '@/hooks/useTranslationUtils';
import { Button } from '@/components/ui/button';
import { Download } from 'lucide-react';

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

const PwaInstaller: React.FC = () => {
  const { t } = useTranslationUtils();
  const [installPrompt, setInstallPrompt] = useState<BeforeInstallPromptEvent | null>(null);

  // Listen for the beforeinstallprompt event
  useEffect(() => {
    const handleBeforeInstallPrompt = (e: Event) => {
      // Prevent the mini-infobar from appearing on mobile
      e.preventDefault();
      // Store the event for later use
      setInstallPrompt(e as BeforeInstallPromptEvent);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    };
  }, []);

  const handleInstallClick = async () => {
    if (!installPrompt) return;

    // Show the install prompt
    await installPrompt.prompt();
    
    // Wait for the user to respond to the prompt
    const choiceResult = await installPrompt.userChoice;
    
    // Reset the installPrompt after the prompt has been used
    setInstallPrompt(null);
    
    // Optionally track the outcome
    if (choiceResult.outcome === 'accepted') {
      console.log('User accepted the install prompt');
    } else {
      console.log('User dismissed the install prompt');
    }
  };

  // Only show the button if the installation prompt is available
  if (!installPrompt) return null;

  return (
    <div className="fixed bottom-20 right-4 z-50">
      <Button 
        onClick={handleInstallClick}
        className="shadow-lg rounded-full px-4 py-2 bg-primary flex items-center gap-2"
      >
        <Download className="h-4 w-4" />
        {t('common:installApp')}
      </Button>
    </div>
  );
};

export default PwaInstaller;
